import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box,
  HStack,
  Stack,
  Button,
  IconButton,
  useToast 
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import ImageWidget from '../../../CommonComponents/ImageWidget';
import { createNewInsight, getInsightData, updateInsightData } from '../../../services/Insights';
import { reverseTransformData, transformData, transformOptions } from '../../../util/helper';
import TypeaheadWidget from '../../../CommonComponents/TypeaheadWidget';
import { getResearchCategoriesAPI } from '../../../services/Films';
import ImagePreview from '../../../components/ImagePreview';
import FormLoader from '../../../CommonComponents/FormLoader';
import { handleApiError, showSuccessToast } from '../../../util/errorHandler';
import { useSelector } from 'react-redux';

const Section1 = () => {

  const { alias } = useParams();
  const [quickSave, setQuickSave] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector(state => state.preview.isEnabled);
  const toast = useToast();
  const navigate = useNavigate();

  // Custom widget for insight image with preview icon - DISABLED DUE TO URL ERROR
  // eslint-disable-next-line no-unused-vars
  const InsightImageWidget = useCallback((props) => {
    const { value } = props;
    
    const handleTogglePreview = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setShowImagePreview(prev => !prev);
    };
    
    return (
      <HStack spacing={2}>
        <Box flex={1}>
          {/* Use default RJSF array widget instead of custom ImageWidget */}\n          <div>\n            {JSON.stringify(value)}\n          </div>
        </Box>
        {value && value.length > 0 && value[0]?.image && (
          <IconButton
            aria-label={showImagePreview ? "Hide image preview" : "Show image preview"}
            icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
            size="sm"
            colorScheme={showImagePreview ? "red" : "blue"}
            onClick={handleTogglePreview}
            title={showImagePreview ? "Close image preview" : "Open image preview"}
          />
        )}
      </HStack>
    );
  }, [showImagePreview, setShowImagePreview]);

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = ['_id', 'insight_alias', 'insight_heading_in_hindi', 'insight_heading', 'insight_sub_heading', 'post_alias', 'insight_number', 'insight_images_alias', 'insight_video_links', 'insight_text_hyperlink', 'masterlist_alias', 'insight_number_of_images', 'insight_text_notes'];
    const rightFields = ['masterlist_tag', 'masterlist_tag_alias', 'keyword_masterlist_tag', 'keyword_masterlist_tag_alias', 'provisional_masterlists_tag', 'provisional_masterlists_tag_alias', 'research_category_tag', 'research_category_tag_alias', 'essential_reading', 'suggested_reading'];
    
    const leftData = {};
    const rightData = {};
    
    leftFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });
    
    rightFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });
    
    return { leftData, rightData };
  };
  
  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Left column schema
  const leftColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "insight_alias": {
        "type": "string",
        "title": "Insight alias",
        "default": ""
      },
      "insight_heading_in_hindi": {
        "type": "string",
        "title": "Insight heading in Hindi",
        "default": ""
      },
      "insight_heading": {
        "type": "string",
        "title": "Insight heading",
        "default": ""
      },
      "insight_sub_heading": {
        "type": "string",
        "title": "Insight sub-heading",
        "default": ""
      },
      "post_alias": {
        "type": "string",
        "title": "Post alias",
        "default": ""
      },
      "insight_number": {
        "type": "number",
        "title": "Insight number",
        "default": 0
      },
      "insight_images_alias": {
        "type": "array",
        "title": "Insight images alias",
        "items": {
          "type": "object",
          "properties": {
            "image_alias": {
              "type": "string",
              "title": "Image Alias",
              "default":""
            },
            "image": {
              "type": "string",
              "title": "Image",
              "default":""
            },
          }
        }
      },
      "insight_video_links": {
        "type": "string",
        "title": "Insight video links",
        "default": ""
      },
      "insight_text_hyperlink": {
        "type": "string",
        "title": "Insight text hyperlink",
        "default": ""
      },
      "masterlist_alias": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Masterlist alias",
      },
      "insight_number_of_images": {
        "type": "number",
        "title": "Insight number of images",
        default: 0
      },
      "insight_text_notes": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Insight text notes",
        "default": []
      },
    },
    required: ["insight_alias", "insight_heading"]
  };

  // Right column schema
  const rightColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "masterlist_tag": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Masterlist tag",
        "default": []
      },
      "masterlist_tag_alias": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Masterlist tag alias",
        "default": []
      },
      "keyword_masterlist_tag": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Keyword masterlist tag",
        "default": []
      },
      "keyword_masterlist_tag_alias": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Keyword masterlist tag alias",
        "default": []
      },
      "provisional_masterlists_tag": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Provisional masterlists tag",
        "default": []
      },
      "provisional_masterlists_tag_alias": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Provisional masterlists tag alias",
        "default": []
      },
      "research_category_tag": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Research category tag",
        "default": []
      },
      "research_category_tag_alias": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Research category tag alias",
        "default": []
      },
      "essential_reading": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Essential reading",
        "default": []
      },
      "suggested_reading": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Suggested reading",
        "default": []
      }
    }
  };

  // Combined schema for preview mode
  const combinedSchema = {
    "title": "Insight fields",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "insight_alias": {
        "type": "string",
        "title": "Insight alias",
        "default": ""
      },
      "insight_heading_in_hindi": {
        "type": "string",
        "title": "Insight heading in Hindi",
        "default": ""
      },
      "insight_heading": {
        "type": "string",
        "title": "Insight heading",
        "default": ""
      },
      "insight_sub_heading": {
        "type": "string",
        "title": "Insight sub-heading",
        "default": ""
      },
      "post_alias": {
        "type": "string",
        "title": "Post alias",
        "default": ""
      },
      "insight_number": {
        "type": "number",
        "title": "Insight number",
        "default": 0
      },
      "insight_video_links": {
        "type": "string",
        "title": "Insight video links",
        "default": ""
      },
      "insight_text_hyperlink": {
        "type": "string",
        "title": "Insight text hyperlink",
        "default": ""
      },
      "masterlist_alias": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Masterlist alias",
      },
      "insight_number_of_images": {
        "type": "number",
        "title": "Insight number of images",
        default: 0
      },
      "insight_images_alias": {
        "type": "array",
        "title": "Insight images alias",
        "items": {
        "type": "object",
        "properties": {
          "image_alias": {
            "type": "string",
            "title": "Image Alias",
            "default":""
          },
          "image": {
            "type": "string",
            "title": "Image",
            "default":""
          },
        }
      }
      },
      "insight_text_notes": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Insight text notes",
        "default": []
      },
      "masterlist_tag": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Masterlist tag",
        "default": []
      },
      "masterlist_tag_alias": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Masterlist tag alias",
        "default": []
      },
      "keyword_masterlist_tag": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Keyword masterlist tag",
        "default": []
      },
      "keyword_masterlist_tag_alias": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Keyword masterlist tag alias",
        "default": []
      },
      "provisional_masterlists_tag": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Provisional masterlists tag",
        "default": []
      },
      "provisional_masterlists_tag_alias": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Provisional masterlists tag alias",
        "default": []
      },
      "research_category_tag": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Research category tag",
        "default": []
      },
      "research_category_tag_alias": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Research category tag alias",
        "default": []
      },
      "essential_reading": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Essential reading",
        "default": []
      },
      "suggested_reading": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Suggested reading",
        "default": []
      }
    },
    required: ["insight_alias", "insight_heading"]
  };
      
  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    insight_alias: {
      'ui:emptyValue': '',
      'ui:readonly': !!alias,
    },
      "insight_images_alias": {
        items: {
          image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    insight_heading_in_hindi: {
      'ui:emptyValue': '',
    },
    insight_heading: {
      'ui:emptyValue': '',
    },
    insight_sub_heading: {
      'ui:emptyValue': '',
    },
    post_alias: {
      'ui:emptyValue': '',
    },
    insight_number: {
      'ui:emptyValue': '',
    },
    insight_video_links: {
      'ui:emptyValue': '',
    },
    insight_text_hyperlink: {
      'ui:emptyValue': '',
    },
    masterlist_alias: {
      'ui:emptyValue': [],
    },
    insight_number_of_images: {
      'ui:emptyValue': 0,
    },
    insight_text_notes: {
      'ui:emptyValue': [],
      items: {
        "ui:widget": "textarea",
      }
    },
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "ui:style": {
      fontSize: "14px",
      },
      research_category_tag: {
        items: {
        "ui:widget": "typeahead1",
      },
    },
    research_category_tag_alias: {
      items: {
        "ui:widget": "typeahead",
      },
    },
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    insight_alias: {
      'ui:emptyValue': '',
      'ui:readonly': !!alias,
    },
    "insight_images_alias": {
      items: {
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    research_category_tag: {
      items: {
        "ui:widget": "typeahead1",
        },
      },
      research_category_tag_alias: {
        items: {
        "ui:widget": "typeahead",
        },
      },
      insight_text_notes: {
        items: {
        "ui:widget": "textarea",
        }
      },
      insight_heading_in_hindi: {
        'ui:emptyValue': '',
      },
      insight_heading: {
        'ui:emptyValue': '',
      },
      insight_sub_heading: {
        'ui:emptyValue': '',
      },
      post_alias: {
        'ui:emptyValue': '',
      },
      insight_number: {
        'ui:emptyValue': '',
      },
      insight_video_links: {
        'ui:emptyValue': '',
      },
      insight_text_hyperlink: {
        'ui:emptyValue': '',
      },
      insight_number_of_images: {
        'ui:emptyValue': '',
      }
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

    const widgets = useMemo(() => ({
      typeahead: (props) => (
        <TypeaheadWidget {...props} fetchOptions={(inputValue) => fetchCategories(inputValue, "alias")} />
      ),
      typeahead1: (props) => (
        <TypeaheadWidget {...props} fetchOptions={(inputValue) => fetchCategories(inputValue, "value")} />
      ),
    }), []);


  const fetchCategories = async(inputValue, keyForLabelValue = "alias") => 
  {
      const result = await getResearchCategoriesAPI();
      const transformedOptions = transformOptions(result, keyForLabelValue);

      // This function return the options
      return transformedOptions.filter((option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
  }

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getInsightData(alias);
          if (result) {
            const fieldsToTransform = [
              'insight_images_alias'
            ];

            // To Show image on form need to provide url to image field which is only used to show the image
            const data = transformData(result?.data?.[0], fieldsToTransform);
            
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data"
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[alias, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      let finalFormData;
      
      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

    const fieldsToTransform = [
      'insight_images_alias'
    ];

      const updatedFormData = reverseTransformData(finalFormData, fieldsToTransform);
      
      // Remove _id from create operations
      if (!alias && updatedFormData._id) {
        delete updatedFormData._id;
      }

      if (alias) {
        const result = await updateInsightData(alias, updatedFormData);
        if (result?.status === 200) {
          setQuickSave(prev => !prev);
            showSuccessToast(toast, "Data updated successfully");
        }
      } else {
        const result = await createNewInsight(updatedFormData);
        if (result?.status === 201) {
          setQuickSave(prev => !prev);
          showSuccessToast(toast, "New insight created successfully");
          navigate(`/insights/${result?.data?.insight_alias}`);
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">Insight Fields</Box>
                  {combinedFormData?.insight_images_alias?.[0]?.image && (
                    <IconButton
                      aria-label={showImagePreview ? "Hide image preview" : "Show image preview"}
                      icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                      size="sm"
                      colorScheme={showImagePreview ? "red" : "blue"}
                      onClick={() => setShowImagePreview(prev => !prev)}
                      title={showImagePreview ? "Close image preview" : "Open image preview"}
                    />
                  )}
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  widgets={widgets}
                  fontsize={"14px"}
                />
              </Box>
              {/* Third column for image preview when clicked */}
              {showImagePreview && combinedFormData?.insight_images_alias?.[0]?.image && (
                <HStack sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}>
                  <ImagePreview image={combinedFormData?.insight_images_alias?.[0]?.image} />
                </HStack>
              )}
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Stack flexDirection="row" gap={4} alignItems="flex-start" flex={1}>
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">Insight Fields</Box>
                      {leftFormData?.insight_images_alias?.[0]?.image && (
                        <IconButton
                          aria-label={showImagePreview ? "Hide image preview" : "Show image preview"}
                          icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                          size="sm"
                          colorScheme={showImagePreview ? "red" : "blue"}
                          onClick={() => setShowImagePreview(prev => !prev)}
                          title={showImagePreview ? "Close image preview" : "Open image preview"}
                        />
                      )}
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
            <Form 
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
              validator={validator}  
                      onChange={handleRightFormChange}
              widgets={widgets}
                      fontsize={"14px"}
              />
          </Box>
        </HStack>
              </Stack>
              {/* Third column for image preview when clicked */}
              {showImagePreview && leftFormData?.insight_images_alias?.[0]?.image && (
                <HStack sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}>
                  <ImagePreview image={leftFormData?.insight_images_alias?.[0]?.image} />
        </HStack>
              )}
            </Box>
          )}
        </Box>
      )}
      
      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Section1;